'use client';

import clsx from 'clsx';
import type { ForwardedRef } from 'react';
import { Checkbox as RACCheckbox, type CheckboxProps as RACCheckboxProps } from 'react-aria-components';
import { forwardRef } from '../../forward-ref.js';
import { useProductionWarning } from '../../utils/use-production-warning.js';
import { withStyleProps } from '../../with-style-props.js';
import { CheckboxIndicator } from '../internal/checkbox-indicator/checkbox-indicator.js';
import type { AriaLabelingProps, CommonProps, StringLikeChildren } from '../types.js';
import { Text } from '../typography/text.js';

export interface CheckboxProps
  extends
    CommonProps,
    AriaLabelingProps,
    Pick<
      RACCheckboxProps,
      | 'onChange'
      | 'isSelected'
      | 'isIndeterminate'
      | 'isDisabled'
      | 'isReadOnly'
      | 'isRequired'
      | 'isInvalid'
      | 'form'
      | 'name'
      | 'value'
      | 'defaultSelected'
      | 'autoFocus'
    > {
  /** The label rendered next to the checkbox. */
  children?: StringLikeChildren;
}

function Checkbox(
  { children, UNSAFE_className, UNSAFE_style, ...props }: CheckboxProps,
  ref: ForwardedRef<HTMLLabelElement>,
) {
  const { 'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledBy } = props;

  useProductionWarning(() => {
    if (!children && !ariaLabel && !ariaLabelledBy) {
      console.warn('Checkbox requires one of children / aria-label / aria-labelledby for accessibility');
    }
  }, [children, ariaLabel, ariaLabelledBy]);

  return (
    <RACCheckbox {...props} ref={ref} className={clsx('cim-checkbox', UNSAFE_className)} style={UNSAFE_style}>
      {({ isIndeterminate, isSelected, isDisabled, isReadOnly, isInvalid, isHovered, isPressed, isFocusVisible }) => (
        <>
          <CheckboxIndicator
            isIndeterminate={isIndeterminate}
            isSelected={isSelected}
            isDisabled={isDisabled}
            isReadOnly={isReadOnly}
            isInvalid={isInvalid}
            isHovered={isHovered}
            isPressed={isPressed}
            isFocusVisible={isFocusVisible}
          />
          {children && (
            <Text as="span" variant="medium">
              {children}
            </Text>
          )}
        </>
      )}
    </RACCheckbox>
  );
}

/**
 * Allows users to mark an item as selected. Can be used standalone, or as part of `CheckboxGroup`.
 *
 * See [checkbox usage guidelines](https://ui.cimpress.io/components/checkbox/).
 */
const _Checkbox = withStyleProps(forwardRef(Checkbox), 'Checkbox');

export { _Checkbox as Checkbox };
